import { useMatches } from 'react-router-dom'
import { RoleProtectedRoute } from './RoleProtectedRoute'
import type { RoleType } from '../auth.types'

/**
 * Automatically applies role protection based on route's handle.roles
 * 
 * This component reads the current route's handle.roles property and
 * automatically wraps the route with RoleProtectedRoute.
 * 
 * Use this in your router configuration to enable route-level role protection
 * without needing to use withRoleAccess HOC in components.
 * 
 * @example
 * ```tsx
 * // In your route configuration:
 * {
 *   path: '/dashboard',
 *   element: (
 *     <RouteRoleGuard>
 *       <DashboardPage />
 *     </RouteRoleGuard>
 *   ),
 *   handle: {
 *     title: 'Dashboard',
 *     roles: ['support', 'super_admin']
 *   }
 * }
 * ```
 */
export function RouteRoleGuard({ children }: { children: React.ReactNode }) {
  const matches = useMatches()
  
  // Get the current route match (last match is the current route)
  const currentMatch = matches[matches.length - 1]
  
  // Extract roles from route handle
  const routeHandle = currentMatch?.handle as { roles?: RoleType[] } | undefined
  const allowedRoles = routeHandle?.roles
  
  // If no roles are specified in the route handle, allow access
  // (route is not role-protected)
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>
  }
  
  // Apply role protection using the roles from route handle
  return (
    <RoleProtectedRoute allowedRoles={allowedRoles}>
      {children}
    </RoleProtectedRoute>
  )
}

